import { NgClass } from '@angular/common'
import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { CreatePersonaModalComponent } from '@app/v2/personas/create-persona-modal/create-persona-modal.component'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { finalize } from 'rxjs'

@Component({
  selector: 'app-empty-data-table',
  templateUrl: './empty-data-table.component.html',
  standalone: true,
  imports: [CreatePersonaModalComponent, NgClass],
  providers: [PersonaService]
})
export class EmptyDataTableComponent {
  readonly eventId = input.required<number>()
  readonly personaCreated = output<void>()

  readonly createPersonaModalId = crypto.randomUUID()
  readonly isCreatingPersona = signal(false)
  readonly createPersonaApiError = signal<unknown | null>(null)
  readonly canCreatePersona = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canModifyEventOrPersonas()
  })

  private readonly _modalService = inject(ModalService)
  private readonly _personaService = inject(PersonaService)
  private readonly _authService = inject(AuthService)
  private readonly _alertService = inject(AlertService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _userRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null })

  onCreatePersonaClick(): void {
    if (!this.canCreatePersona()) return
    this.createPersonaApiError.set(null)
    this._modalService.toggleModal(this.createPersonaModalId)
  }

  isModalOpened(modalId: string): boolean {
    return this._modalService.isModalOpen(modalId)
  }

  cancelCreatePersonaModal(): void {
    if (this._modalService.isModalOpen(this.createPersonaModalId)) {
      this._modalService.toggleModal(this.createPersonaModalId)
    }
  }

  onCreatePersonaSubmitted(name: string): void {
    const trimmed = name.trim()
    if (!trimmed) return

    const workspaceId = this._authService.currentWorkspace?.id
    const eventId = this.eventId()
    if (!workspaceId || !eventId) return

    this.isCreatingPersona.set(true)
    this.createPersonaApiError.set(null)

    this._personaService
      .createPersona(workspaceId, eventId, trimmed)
      .pipe(
        finalize(() => this.isCreatingPersona.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this._alertService.alert(Alert.SUCCESS, res.message)
          this.cancelCreatePersonaModal()
          this.personaCreated.emit()
        },
        error: (err) => {
          const body = err?.error ?? null
          if (this._extractFieldErrors(body, 'name').length) {
            this.createPersonaApiError.set(body)
            return
          }

          this._alertService.alert(Alert.ERROR, this._extractErrorMessage(body, 'Could not create persona. Please try again.'))
        }
      })
  }

  private _extractFieldErrors(body: unknown, field: string): string[] {
    if (!body || typeof body !== 'object' || Array.isArray(body)) return []
    const candidate = (body as Record<string, unknown>)[field]
    if (!Array.isArray(candidate)) return []
    return candidate.filter((message): message is string => typeof message === 'string')
  }

  private _extractErrorMessage(body: unknown, fallback: string): string {
    if (typeof body === 'object' && body !== null && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
      return (body as { message: string }).message
    }
    return fallback
  }
}
